<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { relativeTimeLabel } from '@/domain/store-setup/relative-time';
  import type { IStoreSetupResume } from '@/interfaces/IStoreSetup';

  /**
   * «Tiene una tienda sin terminar» (plan §4.12): shown on `/new` before the
   * flow and at the top of `/dashboard`.
   */
  const props = defineProps<{ resume: IStoreSetupResume }>();
  const emit = defineEmits<{
    (e: 'continue'): void;
    (e: 'dismiss'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;

  const when = computed(() =>
    relativeTimeLabel(props.resume.createdAt, new Date(), proxy.$i18n.locale)
  );
</script>

<template>
  <section class="v2-setup-resume-card">
    <div class="card-head">
      <span class="card-icon" aria-hidden="true">
        <PosUiIcon name="storefront-outline" :size="22" />
      </span>
      <div class="card-text">
        <h2 class="card-title">{{ $t('storeSetup.resume.title') }}</h2>
        <p class="card-row">
          {{ $t('storeSetup.resume.row', { name: resume.name, when }) }}
        </p>
      </div>
    </div>
    <div class="card-actions">
      <PosUiButton class="card-continue" @click="emit('continue')">{{
        $t('storeSetup.resume.continue', { name: resume.name })
      }}</PosUiButton>
      <PosUiButton
        class="card-new"
        color="neutral"
        variant="ghost"
        @click="emit('dismiss')"
        >{{ $t('storeSetup.resume.new') }}</PosUiButton
      >
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-setup-resume-card {
    @apply tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 md:tw-p-5;
    .card-head {
      @apply tw-flex tw-items-start tw-gap-3;
    }
    .card-icon {
      @apply tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-neutral-700;
    }
    .card-text {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5;
    }
    .card-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .card-row {
      @apply tw-m-0 tw-break-words tw-text-sm tw-text-neutral-500;
    }
    .card-actions {
      @apply tw-flex tw-flex-col-reverse tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-end;
    }
    // Store names can be long: the CTA wraps instead of overflowing.
    .card-continue,
    .card-new {
      @apply tw-h-auto tw-min-h-11 tw-min-w-0 tw-max-w-full tw-whitespace-normal tw-py-2 tw-text-center;
    }
  }
</style>
